"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { Suspense, useEffect, useRef, useState } from "react"
import posthog from "posthog-js"
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react"

import {
  getPageViewCaptureDecision,
  POSTHOG_DEFAULTS_DATE,
  POSTHOG_UI_HOST,
  runOnce,
} from "@/lib/posthog/clientConfig"
import {
  getAuthenticatedPostHogDistinctId,
  persistAuthenticatedPostHogDistinctId,
  readAuthenticatedPostHogDistinctId,
  resetPostHogIdentity,
} from "@/lib/posthog/identity"
import type { TLoginUserData } from "@/schemas/user.schema"

function buildPostHogPageViewUrl(pathname: string, search: string) {
  const url = new URL(pathname || "/", window.location.origin)
  if (search) {
    url.search = search
  }
  return url.toString()
}

export function PostHogProvider({
  children,
  initialUserData,
}: {
  children: React.ReactNode
  initialUserData: TLoginUserData
}) {
  const [initialPageViewUrl, setInitialPageViewUrl] = useState<string | null>(
    null
  )
  const [isReady, setIsReady] = useState(false)
  const hasInitializedRef = useRef(false)
  const authenticatedDistinctId = getAuthenticatedPostHogDistinctId({
    accountStatus: initialUserData.account.status,
    userId: initialUserData.user.id,
  })

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    if (!key) {
      return
    }

    if (hasInitializedRef.current) {
      return
    }
    hasInitializedRef.current = true

    const markReady = runOnce((client: typeof posthog) => {
      if (authenticatedDistinctId) {
        client.identify(authenticatedDistinctId, {
          email: initialUserData.user.email,
          firstName: initialUserData.user.name,
          lastName: initialUserData.user.lastName,
          companyName: initialUserData.user.companyName,
          ringioAccountId: initialUserData.user.accountId,
        })
        client.group("company", initialUserData.user.accountId.toString(), {
          name: initialUserData.user.companyName,
        })
        persistAuthenticatedPostHogDistinctId(
          window.localStorage,
          authenticatedDistinctId
        )
      } else if (readAuthenticatedPostHogDistinctId(window.localStorage)) {
        resetPostHogIdentity(client, window.localStorage)
      }

      const currentUrl = buildPostHogPageViewUrl(
        window.location.pathname,
        window.location.search
      )
      client.capture("$pageview", { $current_url: currentUrl })
      setInitialPageViewUrl(currentUrl)
      setIsReady(true)
    })

    posthog.init(key, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://l.ring.io",
      ui_host: POSTHOG_UI_HOST,
      defaults: POSTHOG_DEFAULTS_DATE,
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_pageleave: true,
      loaded: (client) => markReady(client as typeof posthog),
    })

    if ("__loaded" in posthog && posthog.__loaded) {
      markReady(posthog)
    }
  }, [authenticatedDistinctId, initialUserData])

  return (
    <PHProvider client={posthog}>
      {isReady ? (
        <SuspendedPostHogPageView initialPageViewUrl={initialPageViewUrl} />
      ) : null}
      {children}
    </PHProvider>
  )
}

function PostHogPageView({
  initialPageViewUrl,
}: {
  initialPageViewUrl: string | null
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const client = usePostHog()
  const hasHandledInitialPageViewRef = useRef(false)

  useEffect(() => {
    if (!pathname || !client) {
      return
    }

    const currentUrl = buildPostHogPageViewUrl(pathname, searchParams.toString())
    const decision = getPageViewCaptureDecision({
      currentUrl,
      hasHandledInitialPageView: hasHandledInitialPageViewRef.current,
      initialPageViewUrl,
    })
    hasHandledInitialPageViewRef.current = decision.hasHandledInitialPageView

    if (decision.capture) {
      client.capture("$pageview", { $current_url: currentUrl })
    }
  }, [client, initialPageViewUrl, pathname, searchParams])

  return null
}

function SuspendedPostHogPageView({
  initialPageViewUrl,
}: {
  initialPageViewUrl: string | null
}) {
  return (
    <Suspense fallback={null}>
      <PostHogPageView initialPageViewUrl={initialPageViewUrl} />
    </Suspense>
  )
}
